// What one set is worth to one person, before any solver looks at it.
//
// Three things go into it and they are kept separate because they answer to
// different parts of the interface: the rating itself, the rank bonus from the top
// ten, and the objective weights from onboarding. Collapsing them into one number
// too early is what makes a recommendation impossible to explain, and an
// unexplainable recommendation gets ignored.

import type { TuningConfig } from './config.ts';
import { EMOTIONS, preferenceValue } from './preferences.ts';
import type { Artist, FestivalSet, Id, MustSee, Note, Profile, Rating } from './types.ts';

export interface UtilityInputs {
  profile: Profile;
  ratings: Map<Id, Rating>;
  mustSee: MustSee[];
  artists: Map<Id, Artist>;
  cfg: TuningConfig;
  /** 0 for the first festival day, 1 for the second. Drives stamina decay. */
  dayIndex: number;
  /** Notes by artist, read only for confirmed tags and an explicit seen-before. */
  notes?: Map<Id, Note>;
  /** Signed, applied to an artist marked unseen. Zero unless the user chose it. */
  noveltyWeight?: number;
  /** Ceiling on what a confirmed emotional tag can move a set. */
  emotionalWeight?: number;
}

/** A rating on the 1-10 scale expressed on 0-1, which is what everything else uses. */
export function scoreToBase(score: number): number {
  return Math.max(0, Math.min(1, (score - 1) / 9));
}

export function rankBonus(rank: number | undefined, cfg: TuningConfig): number {
  if (!rank || rank < 1) return 0;
  return cfg.rankBonusB * Math.pow(cfg.rankBonusLambda, rank - 1);
}

/**
 * Late sets are worth less on later nights, by however much the stamina answer
 * says. Applied to the closing hours of a day rather than the whole day, because
 * the thing that gets sacrificed on night three is the 2am set, not the 6pm one.
 */
export function staminaMultiplier(
  set: FestivalSet,
  inputs: UtilityInputs,
): number {
  const profile = inputs.cfg.staminaProfiles[inputs.profile.staminaCurve];
  if (!profile) return 1;
  const hour = new Date(set.startTs).getHours();
  const isLate = hour >= 23 || hour < 6;
  if (!isLate) return 1;
  return Math.pow(profile.nightlyDecay, inputs.dayIndex);
}

/** u_full for one set: what attending the whole thing is worth to this person. */
export function setUtility(set: FestivalSet, inputs: UtilityInputs): number {
  const rating = inputs.ratings.get(set.artistId);
  const goal = inputs.cfg.goalWeights[inputs.profile.goalPriority] ?? inputs.cfg.goalWeights.max_music;
  const confidence = rating ? rating.confidence : inputs.cfg.seedConfidence;

  // The rating is the base. A confirmed tag and an explicit discovery weight
  // adjust it within their own bounds; an unconfirmed suggestion does nothing,
  // and an unrated artist keeps the seeded stand-in rather than inheriting a
  // number from a sentence.
  const note = inputs.notes?.get(set.artistId);
  const base =
    rating && note
      ? (preferenceValue(
          {
            score: Math.max(1, Math.min(10, Math.round(rating.score))),
            seenBefore: note.seenBefore ?? 'unknown',
            note: '',
            tags: (note.tags ?? []).filter((t) => t.confirmed && EMOTIONS.has(t.name)),
          },
          {
            noveltyWeight: inputs.noveltyWeight ?? 0,
            emotionalWeight: inputs.emotionalWeight ?? 0.15,
          },
        ).value ?? scoreToBase(rating.score))
      : rating
        ? scoreToBase(rating.score)
        : scoreToBase(inputs.cfg.seedScores.unmatched);

  const ms = inputs.mustSee.find((m) => m.artistId === set.artistId);
  const bonus = rankBonus(ms?.rank, inputs.cfg);

  // Something the user has not pinned down is worth more when the weekend is for
  // discovery and worth less when it is for headliners.
  const discovery = goal.discovery * (1 - confidence) * 0.15;

  return (goal.music * base + discovery + bonus) * staminaMultiplier(set, inputs);
}

export function buildUtilityMap(sets: FestivalSet[], inputs: UtilityInputs): Map<Id, number> {
  const out = new Map<Id, number>();
  for (const s of sets) out.set(s.id, setUtility(s, inputs));
  return out;
}

/** Seeded ratings for every artist on the bill, from the subgenre answers alone. */
export function seedRatings(
  userId: Id,
  artists: Artist[],
  subgenreWeights: Record<string, string>,
  cfg: TuningConfig,
  now = new Date().toISOString(),
): Rating[] {
  return artists.map((a) => {
    const weights = a.genreTags
      .map((t) => subgenreWeights[t])
      .filter((w): w is string => Boolean(w));
    let score: number;
    if (weights.includes('love')) score = cfg.seedScores.love;
    else if (weights.includes('like')) score = cfg.seedScores.like;
    else if (weights.length > 0) score = cfg.seedScores.no;
    else score = cfg.seedScores.unmatched;

    // A tag that matches two answers is firmer evidence than one that matches none.
    const spread = weights.length === 0 ? 0 : Math.min(0.15, 0.05 * weights.length);
    return {
      userId,
      artistId: a.id,
      score,
      source: 'seeded' as const,
      confidence: cfg.seedConfidence + spread,
      updatedAt: now,
    };
  });
}

/** Rest windows the stamina answer requires, one per night, placed in the evening lull. */
export function requiredRestMinutes(profile: Profile, cfg: TuningConfig): number {
  return cfg.staminaProfiles[profile.staminaCurve]?.restMinutesPerNight ?? 0;
}
